"use client";

import { ChevronRight } from "lucide-react";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import type { ArticleItem } from "./ArticleCard";
import { resolveLinkHref } from "../../utils/links";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import {
  getImageObjectPositionStyle,
  getImageOverrideProps,
  mergeClassNames,
  mergeStyles,
  resolveImageValue,
} from "../../utils/imageOptions";

/**
 * Compact news-website article card used by `LatestArticleGrid` when
 * `cardStyle === "compact"`. Built to read as a "related articles" /
 * "À lire aussi" footer on an article page — the call sites where the
 * full `ArticleCard` (category pill + duplicated hero-as-avatar + three
 * placeholder social icons) reads as too much chrome.
 *
 * Layout pinned by visible-design choice:
 *   - hero image full-width at the top
 *   - title (brand blue, bold) immediately under, clickable
 *   - 3-line excerpt clamp for visual rhythm — falls through silently
 *     when the source has no `summary`
 *   - bottom row: `author · date` inline grey meta + right-aligned
 *     chevron-in-circle as the navigation affordance
 *
 * Reuses the same `ArticleItem` type as `ArticleCard` so the
 * projector's existing field mapping (`title`, `excerpt`, `author`,
 * `date`, `image`, `href`) keeps working — only `category`,
 * `authorRole`, and the social-icon placeholders are intentionally
 * unused here.
 */
export function ArticleCardCompact({
  title,
  excerpt,
  author,
  date,
  href,
  imageSrc,
  portrait,
  imagePosition,
  imageZoom,
}: ArticleItem & {
  /** Portrait image ratio (`aspect-[4/5]`) instead of the wide fixed `h-56` band.
   *  Set ONLY by the gouverneur "Historique récent" strip so governor portraits read
   *  well in the narrower 4-up grid; every other compact strip keeps the wide image. */
  portrait?: boolean;
  /** CSS `object-position` for the portrait crop (face-biased). Passed by the
   *  gouverneur strip so the same focal point used on the detail-page hero keeps the
   *  face visible inside the narrow card. A stored CMS focal point still wins. */
  imagePosition?: string;
  /** In-frame zoom (transform scale) for the portrait crop. Passed by the gouverneur
   *  strip so a full-body source photo reads as an upper-body portrait, matching the
   *  detail-page hero. `undefined`/`1` = no zoom. Contained by the link's overflow. */
  imageZoom?: number;
}) {
  const locale = useLocale();
  const { src, options } = resolveImageValue(imageSrc);
  // No silent placeholder fallback: data-record histories (cours-de-change,
  // secteur-extérieur, etc.) carry no editorial image. Rendering a generic
  // welcome-banner PNG for every card made the "Historique récent" grid look
  // broken. When there is no real image, render an image-less card — a tile
  // built around title + meta + chevron, sized to read as part of the same
  // grid as image cards.
  const hasImage = Boolean(src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  // Face-biased crop base for portrait (governor) cards; a stored CMS focal point
  // still overrides it (merged after). No effect on the wide non-portrait cards.
  const portraitPositionStyle = imagePosition ? { objectPosition: imagePosition } : undefined;
  // Zoom a full-body governor portrait into its head+torso so the small card matches
  // the detail-page hero (clipped by the link's overflow-hidden). Overrides the tiny
  // hover-scale for those cards only — an acceptable trade for consistent framing.
  const portraitZoomStyle =
    imageZoom && imageZoom !== 1
      ? { transform: `scale(${imageZoom})`, transformOrigin: imagePosition || "center" }
      : undefined;
  const safeHref = resolveLinkHref(href) ?? "/";
  const hasExcerpt = Boolean(excerpt && excerpt.trim().length > 0);
  const hasAuthor = Boolean(author && author.trim().length > 0);
  // Normalise the editorial date to one consistent localized style so a listing
  // never shows "2026-02-06" next to "21 Nov 2025"; non-dates pass through.
  const displayDate = formatDisplayDate(date, locale);
  const hasDate = Boolean(displayDate && displayDate.trim().length > 0);
  return (
    <article className="apa-card apa-card-hover group flex h-full flex-col overflow-hidden">
      {hasImage ? (
        <RuntimeLink href={safeHref} className="apa-img-editorial block overflow-hidden bg-slate-100">
          <RuntimeImage
            src={src as string}
            alt=""
            className={mergeClassNames(
              portrait
                ? "aspect-[4/5] w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                : "h-56 w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105",
              overrideProps.className,
            )}
            style={mergeStyles(portraitPositionStyle, portraitZoomStyle, objectPositionStyle, overrideProps.style)}
          />
        </RuntimeLink>
      ) : null}
      <div className="flex flex-1 flex-col p-4">
        <RuntimeLink
          href={safeHref}
          className="block text-lg font-semibold leading-6 text-[#0A0A0A]"
        >
          <span className="apa-underline-reveal">{title}</span>
        </RuntimeLink>
        {hasExcerpt ? (
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[var(--color-muted)]">{excerpt}</p>
        ) : null}
        <div className="mt-auto flex items-center gap-1 pt-3 text-xs text-slate-500">
          {hasAuthor ? <span className="font-medium text-[var(--color-forest-600)]">{author}</span> : null}
          {hasAuthor && hasDate ? <span aria-hidden="true">·</span> : null}
          {hasDate ? <span>{displayDate}</span> : null}
          <RuntimeLink
            href={safeHref}
            aria-label={t(locale, "readArticle")}
            className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-full text-[#00B862] transition hover:bg-[var(--color-forest-100)]"
          >
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </RuntimeLink>
        </div>
      </div>
    </article>
  );
}
